/*
 * Copyright (C) 2020-present Borza LLC. All rights reserved.
 */

import Head from "next/head";
import Link from "next/link";
import { Component } from "react";
import LazyLoad from "react-lazy-load";

import Banner from "../../components/banner";
import Newsletter from "../../components/newsletter";
import OpenGraphHead from "../../components/og-head";
import TwitterHead from "../../components/twitter-head";
import {
  toAbsolutePageURL,
  toRelativePageURL,
} from "../../utils/url";

interface IProps {
  query: {
    market: string;
  };
}

export default class ChildrensCraniofacialAssociation extends Component<IProps> {
  public static getInitialProps({ query }: IProps) {
    query.market = query.market ?? "en-US";

    return { query };
  }

  public render() {
    const {
      query: {
        market,
      },
    } = this.props;

    const canonicalUrl = toAbsolutePageURL("/partners/cca-childrens-craniofacial-association", market);
    const alt = "Lasting Smiles";
    const title = "Children's Craniofacial Association · Partners · Lasting Smiles";
    const description = "Give back to Children's Craniofacial Association with Lasting Smiles organic and all natural lip balm. " +
      "Smile for a cause. " +
      "Give back to charitable causes you care about with sustainable, ethical, and organic skin care products that make a difference all year long. " +
      "The #1 socially responsible and ethical lip care and do good lip balm choice to support your favorite social and environmental non-profit causes caring for people and our planet. " +
      "The perfect gift that gives back.";

    return (
      <>
        <Head>
          <title>{title}</title>
          <meta
            content={description}
            name="description"
          />
          <link
            href={canonicalUrl}
            rel="canonical"
          />
          <link
            href={toAbsolutePageURL("/partners/cca-childrens-craniofacial-association", "en-US")}
            hrefLang="x-default"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/partners/cca-childrens-craniofacial-association", "en-US")}
            hrefLang="en-US"
            rel="alternate"
          />
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/", market),
                    "name": "Lasting Smiles",
                    "position": 1,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/purpose", market),
                    "name": "Causes",
                    "position": 2,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/partners/cca-childrens-craniofacial-association", market),
                    "name": "Children's Craniofacial Association",
                    "position": 3,
                  },
                ],
              }),
            }}
            type="application/ld+json"
          />
        </Head>
        <Banner />
        <Newsletter />
        <section
          className="bg-yellow-100 px-2 py-10 sm:px-5"
          style={{
            backgroundImage: `url("${require("../../public/images/backgrounds/purpose.jpg")}")` as string,
            backgroundPositionY: "center",
            backgroundSize: "cover",
          }}
        >
          <div className="max-w-screen-lg mx-auto">
            <h2
              className="font-extrabold font-serif sm:text-6xl text-5xl text-center uppercase"
              style={{
                background: "linear-gradient(to bottom right, #FAC551, #F36F48, #F3B411, #F7A4B6)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Smile for a&nbsp;Cause
            </h2>
            <picture>
              <source
                srcSet={require("../../public/images/logos/cca-wide.webp") as string}
                type="image/webp"
              />
              <source
                srcSet={require("../../public/images/logos/cca-wide.png") as string}
                type="image/png"
              />
              <img
                alt="Children's Craniofacial Association"
                className="block mx-auto"
                src={require("../../public/images/logos/cca-wide.png") as string}
                style={{ maxHeight: 200 }}
              />
            </picture>
          </div>
        </section>
        <section className="py-10">
          <div className="max-w-screen-lg mx-auto">
            <h1 className="font-medium font-serif px-2 sm:px-5 text-3xl text-center">
              Children's Craniofacial Association
            </h1>
            <p className="px-2 sm:px-5 text-xl text-center">
              Children's Craniofacial Association is a national, 501(c)3 nonprofit organization, headquartered in Dallas, Texas.
              Nationally and internationally, CCA addresses the medical, financial, psychosocial, emotional, and educational concerns relating to craniofacial conditions.
            </p>
            <div className="overflow-x-auto overflow-y-hidden scrolling-touch sm:max-w-screen-md sm:mx-auto sm:px-5">
              <div className="flex-row inline-flex sm:flex sm:flex-wrap sm:items-stretch sm:justify-around text-white">
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/cca/photo-1.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/cca/photo-1.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Children's Craniofacial Association Photo 1"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/cca/photo-1.jpg") as string}
                    />
                  </picture>
                </div>
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/cca/photo-2.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/cca/photo-2.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Children's Craniofacial Association Photo 2"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/cca/photo-2.jpg") as string}
                    />
                  </picture>
                </div>
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/cca/photo-3.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/cca/photo-3.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Children's Craniofacial Association Photo 3"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/cca/photo-3.jpg") as string}
                    />
                  </picture>
                </div>
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/cca/photo-4.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/cca/photo-4.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Children's Craniofacial Association Photo 4"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/cca/photo-4.jpg") as string}
                    />
                  </picture>
                </div>
              </div>
            </div>
            <p className="px-2 sm:px-5 text-xl text-center">
              Each year, more than 100,000 children are either born with or develop a craniofacial condition.
              Their parents and families try to understand what syndrome they have and how to help them.
              While individuals with a facial difference are frequently classified by others as inferior, CCA is changing the narrative and empowering these kids and adults to be self-advocates.
              We offer family networking, social gatherings, an Annual Family Retreat, direction to and financial assistance for quality medical care, and educational resources for schools and communities to learn the values of tolerance, acceptance, and self-love.
            </p>
          </div>
        </section>
        <section className="bg-gray-100 px-2 py-10 sm:px-5">
          <div className="max-w-screen-md mx-auto">
            <h2 className="font-medium font-serif text-2xl text-center">
              Testimonial on Financial Assistance Program
            </h2>
            <p className="text-center">
              &ldquo;We appreciate everything Children's Craniofacial Association (CCA) has done for our family.
              Without the help of CCA we would not have been able to make the multiple trips over 300 miles away to Doernbecher Children's Hospital for our daughter Everly's appointments and surgery.
              Their financial and emotional support has been such a blessing and relief to us.
              With the help of CCA, we were able to stay comfortably for multiple nights in a hotel and focus on what was important, the health and recovery of our beautiful daughter, Everly.&rdquo;
            </p>
            <div className="text-center">
              <Link
                as={toRelativePageURL("/products", market as string)}
                href={{
                  pathname: "/products",
                  query: { market },
                }}
              >
                <a className="bg-black border border-black duration-500 ease-in-out font-medium font-serif hover:bg-default hover:text-black inline-block my-5 px-5 py-3 rounded shadow uppercase text-lg text-white transition">
                  Shop &amp; Make a Difference
                </a>
              </Link>
            </div>
          </div>
        </section>
        <section className="bg-water px-2 py-10 sm:px-5">
          <div className="max-w-screen-md mx-auto">
            <h2 className="font-medium font-serif text-2xl text-center">
              Watch how Children's Craniofacial Association creates lasting smiles by supporting families of kids with craniofacial differences.
            </h2>
            <LazyLoad
              className="min-h-200 relative sm:min-h-400 my-5"
              offset={250}
            >
              <iframe
                allowFullScreen={true}
                className="absolute h-full left-0 top-0 w-full"
                src="https://www.youtube.com/embed/DQnQs4NZszo"
                title="CCA Kids: How We Make A Difference"
              />
            </LazyLoad>
          </div>
          <div className="text-center">
            <Link
              as={toRelativePageURL("/products", market as string)}
              href={{
                pathname: "/products",
                query: { market },
              }}
            >
              <a className="bg-black border border-black duration-500 ease-in-out font-medium font-serif hover:bg-default hover:text-black inline-block my-5 px-5 py-3 rounded shadow uppercase text-lg text-white transition">
                Shop &amp; Make a Difference
              </a>
            </Link>
          </div>
        </section>
      </>
    );
  }
}
